import { Vue } from "vue-property-decorator"
import { CaseDetails, TemplateDetails, CaseField, User } from "@/types"

import api from "@/services/api"
import cloneDeep from "lodash/cloneDeep"
import { caseColumns } from "@/types/caseColumns"
import { TenantFeatures } from "@/types/tenantFeatures"
import { SourceType } from "@/types/sourceType"

const cascadeListChange = async (caseDetails: CaseDetails, caseField: CaseField, newValue: any, getFields: boolean) => {
	if (!caseDetails || !caseField.field || !caseField.field.extensions) return

	if (caseField.field.dataType === "list") {
		// Find any field that depends on this one ...
		Object.keys(caseDetails.fields!).forEach(fieldId => {
			const field = caseDetails.fields![fieldId]
			if (field.field &&
				field.field.extensions &&
				field.field.dataType !== "calculated" &&
				field.field.extensions.dataListField === caseField.field.id) {
				if (newValue.fields && newValue.fields[field.field.extensions.dataItemField] !== undefined) {
					field.value = newValue.fields[field.field.extensions.dataItemField]
					if (caseDetails.id) {
						// Inside a ForEach, so no async allowed! We need to assign field to a const to prevent data changes in the loop.
						const updateField = field
						api.saveCaseField(caseDetails.id, field.field.id!, field.value).then(() => {
							updateField.oldValue = updateField.value
						})
					}
				}
			}
		})
	}

	if (caseField.field.extensions &&
		caseField.field.extensions.listFilterValueField &&
		(Array.isArray(newValue) ? newValue.length : newValue)) {
		const listFilterField = caseDetails.fields![caseField.field.extensions.listFilterValueField] as CaseField
		if (!listFilterField) return

		let valueChanged = false

		let updatedItems = newValue
		if (getFields) {
			// This is for recursive calls. We need to hit up the data service to fill the fields.
			// The fields will be set on first call, because it's from the data source itself, not fields within the source object.
			if (Array.isArray(newValue)) {
				updatedItems = await Promise.all(newValue.map((item: { id: string }) =>
					api.dataSourceGetItem(item.id)
				))
			} else {
				updatedItems = await api.dataSourceGetItem(newValue.id)
			}
		}

		let updatedValue = cloneDeep(listFilterField.value) as any

		// Function to set value on the target field
		const addValue = listFilterField.field.dataType === "list"
			? (id: string, label: string) => {
				// For list type fields, set the value if not set; don't override existing values.
				if (!updatedValue) {
					valueChanged = true
					updatedValue = id
				}
			}
			: (id: string, label: string) => {
				// For list-multiple fields, if null, make a list. Then add if not already there.
				if (!updatedValue) {
					updatedValue = []
				}

				if (!updatedValue.some((v: {id: string, label: string}) => v.id === id)) {
					valueChanged = true
					updatedValue.push({ id, label })
				}
			}

		if (Array.isArray(newValue)) {
			// The value we've just set is an array, so iterate through
			if (updatedItems.length > 0) {
				const newValueFilterValues = updatedItems
					.filter((v: any) => v.fields[caseField.field.extensions!.listFilter])
					.map((v: any) => v.fields[caseField.field.extensions!.listFilter])

				newValueFilterValues.forEach((v: any) => {
					if (Array.isArray(v)) {
						v.forEach((item: {id: string, label: string}) => addValue(item.id, item.label))
					} else {
						addValue(v.id, v.label)
					}
				})
			}
		} else if (updatedItems.fields) {
			// const newValueFilterValue =
			addValue(updatedItems.fields[caseField.field.extensions!.listFilter],
				updatedItems.fields[caseField.field.extensions!.listFilter + "-label"])
		}

		if (caseDetails && valueChanged) {
			// Value changed, so push it onto the object.
			listFilterField.value = updatedValue

			// only try to save if this isn't a new case
			if (caseDetails.id) {
				await api.saveCaseField(caseDetails.id, listFilterField.field.id!, listFilterField.value)
				listFilterField.oldValue = updatedValue
			}

			// Recurse in case this affected other rows
			cascadeListChange(
				caseDetails,
				listFilterField,
				updatedValue,
				true
			)
		}
	}
}

const applyTemplate = (caseDetails: CaseDetails, template: TemplateDetails) => {
	caseDetails.templateId = template.id
	caseDetails.sections = template.sections
	caseDetails.sectionDetails = template.sectionDetails

	if (template.severityId) {
		caseDetails.severityId = template.severityId
	}

	const fieldIds = new Set<string>()
	template.fields.forEach(field => {
		fieldIds.add(field.field.id!)
		const caseField = {
			caseId: caseDetails.id,
			oldValue: null,
			value: null,
			label: null,
			valueObject: null,
			section: field.section,
			order: field.order,
			permissions: field.permissions,
			requiredCase: field.requiredCase,
			requiredRecord: field.requiredRecord,
			field: field.field
		} as CaseField

		Vue.set(caseDetails.fields!, field.field.id!, caseField)
	})

	// Remove all fields that no longer apply
	const caseFieldIds = Object.keys(caseDetails.fields!)
	caseFieldIds.forEach(fieldId => {
		if (!fieldIds.has(fieldId)) {
			Vue.delete(caseDetails.fields!, fieldId)
		}
	})

	caseDetails.cadenceEnabled = !!(template.cadenceDetails &&
		template.cadenceDetails.cadencePeriod !== null &&
		template.cadenceDetails.cadencePeriod !== undefined &&
		template.cadenceDetails.cadenceDefaultSet !== false)
	caseDetails.cadenceDetails = template.cadenceDetails

	if (template.fieldDefaultValues) {
		Object.keys(template.fieldDefaultValues).forEach(fieldId => {
			const fieldInstance = caseDetails.fields[fieldId] as CaseField
			if (fieldInstance) {
				fieldInstance.value = template.fieldDefaultValues![fieldId]

				// Cascade dependany field values
				if (fieldInstance.field.dataType === "list" || fieldInstance.field.dataType === "list-multiple") {
					cascadeListChange(
						caseDetails,
						fieldInstance,
						fieldInstance.value,
						false
					)
				}
			}
		})
	}
}

const updateCaseDetails = (currentCase: CaseDetails, updatedCase: CaseDetails, removeExtraFields: boolean = false) => {
	let templateIdChanged = currentCase.templateId !== updatedCase.templateId

	currentCase.title = updatedCase.title
	currentCase.summary = updatedCase.summary
	currentCase.severityId = updatedCase.severityId
	currentCase.statusId = updatedCase.statusId
	currentCase.pendingTeams = updatedCase.pendingTeams
	currentCase.actions = updatedCase.actions
	currentCase.areas = updatedCase.areas
	currentCase.links = updatedCase.links
	currentCase.isCase = updatedCase.isCase
	currentCase.isClosed = updatedCase.isClosed
	currentCase.cadenceDetails = updatedCase.cadenceDetails
	currentCase.permissions = updatedCase.permissions
	currentCase.builtInFieldLocks = updatedCase.builtInFieldLocks
	currentCase.cadenceEnabled = updatedCase.cadenceEnabled
	currentCase.cadenceDetails = updatedCase.cadenceDetails

	currentCase.lastUpdated = updatedCase.lastUpdated
	currentCase.lastUpdatedAt = updatedCase.lastUpdatedAt
	currentCase.assigneeId = updatedCase.assigneeId
	currentCase.cadenceEnabled = updatedCase.cadenceEnabled

	currentCase.templateId = updatedCase.templateId
	currentCase.templateName = updatedCase.templateName
	currentCase.pendingTriage = updatedCase.pendingTriage
	currentCase.pendingClosure = updatedCase.pendingClosure

	currentCase.sections = updatedCase.sections
	currentCase.sectionDetails = updatedCase.sectionDetails

	if (templateIdChanged || !currentCase.fields) {
		currentCase.fields = updatedCase.fields
	} else if (currentCase.fields && updatedCase.fields) {
		const currentFields = currentCase.fields

		Object.keys(updatedCase.fields).forEach(fieldId => {
			const field = updatedCase.fields![fieldId]
			if (currentFields[field.field.id!]) {
				if (!field.inUseBy) {
					Vue.set(currentFields[field.field.id!], "value", field.value)
					Vue.set(currentFields[field.field.id!], "valueTimezone", field.valueTimezone)
					Vue.set(currentFields[field.field.id!], "oldValue", field.value)
					Vue.set(currentFields[field.field.id!], "label", field.label)
				}

				Vue.set(currentFields[field.field.id!], "inUseBy", field.inUseBy)
				Vue.set(currentFields[field.field.id!].field, "dataType", field.field.dataType)

				currentFields[field.field.id!].permissions = field.permissions
			} else if (currentCase) {
				Vue.set(currentCase.fields!, field.field.id!, field)
			}
		})

		if (removeExtraFields) {
			Object.keys(currentCase.fields).forEach(fieldId => {
				if (!(fieldId in updatedCase.fields)) {
					Vue.delete(currentCase.fields, fieldId)
				}
			})
		}
	}

	currentCase.users = updatedCase.users
	currentCase.tags = updatedCase.tags
}

const applyFieldChanges = (record: CaseDetails, changes: { [key: string]: { label: string, value: any } }) => {
	Object.keys(changes).forEach(key => {
		const change = changes[key]

		if (key.startsWith("fields.")) {
			const fieldId = key.substring(7)
			if (!record.fields) {
				Vue.set(record, "fields", {})
			}
			if (!record.fields[fieldId]) {
				Vue.set(record.fields, fieldId, {})
			}

			Vue.set(record.fields[fieldId], "value", change.value)
			Vue.set(record.fields[fieldId], "label", change.label)
		} else {
			Vue.set(record, key, change.value)
			const column = caseColumns.find(c => c.key === key)
			if (column && column.displayKey) {
				Vue.set(record, column.displayKey, change.label)
			}
		}
	})
}

/* Cadence related functions */
const getIsCadenceVisibleOnCase = (caseDetails: CaseDetails, template: TemplateDetails, getIsFeatureEnabled: (feature: TenantFeatures) => boolean) => {
	return (template && template.cadenceDetails &&
			template.cadenceDetails.cadencePeriod !== null &&
			template.cadenceDetails.cadencePeriod !== undefined) ||
		(!!caseDetails.cadenceId && !!caseDetails.cadenceNumber && getIsFeatureEnabled(TenantFeatures.CadenceId))
}

const getCanUserDisableCadence = (caseDetails: CaseDetails, user: User, template: TemplateDetails, getIsFeatureEnabled: (feature: TenantFeatures) => boolean) => {
	if (!template.cadenceDetails) return false
	const disablePermissions = template.cadenceDetails!.disablePermissions ? template.cadenceDetails!.disablePermissions : []
	return getIsCadenceVisibleOnCase(caseDetails, template, getIsFeatureEnabled) && (!disablePermissions || disablePermissions.length === 0 ||
		disablePermissions.some((t: any) => user.teams.find((team: any) => team.id === t.id)))
}

/**
 * Ensures that the cadence details of a case are correct after it is updated. Updates the provided caseDetails object.
 * @param caseDetails The updated case details to validate.
 * @param template The case's Template.
 * @param getIsFeatureEnabled Method to check if a feature is enabled.
 */
const normalizeCaseCadenceProperties = (caseDetails: CaseDetails, template: TemplateDetails, getIsFeatureEnabled: (feature: TenantFeatures) => boolean) => {
	if (!caseDetails.id) return

	// Add/Remove cadence sourceType if the case is the initially created cadence
	if (!caseDetails.cadenceId && getIsCadenceVisibleOnCase(caseDetails, template, getIsFeatureEnabled)) {
		caseDetails.sourceType = caseDetails.cadenceEnabled ? SourceType.Cadence : null
	}
}

export {
	cascadeListChange,
	applyTemplate,
	updateCaseDetails,
	applyFieldChanges,

	getIsCadenceVisibleOnCase,
	getCanUserDisableCadence,
	normalizeCaseCadenceProperties
}
